import queryString from "query-string";
import pick from "lodash/pick";
import _isEmpty from "lodash/isEmpty";
import _pickBy from "lodash/pickBy";
import _isNil from "lodash/isNil";
import _isArray from "lodash/isArray";
import _isObject from "lodash/isObject";
import _isString from "lodash/isString";
import _camelCase from "lodash/camelCase";
import _snakeCase from "lodash/snakeCase";
import _startCase from "lodash/startCase";
import _lowerCase from "lodash/lowerCase";
import { MIME_TYPES } from "../consts";
import moment from "moment";
import momentTimezone from "moment-timezone";
import { Store } from "react-notifications-component";
import Cookies from "js-cookie";

// Return a query string for the url filters and pages, receives an object, if empty object is received it returns an empty string
export const stringifyQuery = (params) => {
  const _filters = _pickBy(params, (filter) => {
    if (typeof filter === "string") return filter.length > 0;
    return true;
  });
  // Add the query params
  return !_isEmpty(_filters) ? `?${queryString.stringify(_filters)}` : "";
};

// Regresa un objeto con los parametros de query parseados
export const parseQuery = queryString.parse;

/**
 *
 * @param {string | Date | null | undefined} date
 * @returns {string}
 */
export const toHumanDate = (date, format = "DD-MMMM-YYYY") => {
  if (!date) return "-";
  return moment(date).format(format);
};
/**
 *
 * @param {string | Date | null | undefined} date
 * @returns {string}
 */
export const dateToLocaleString = (date) => {
  if (!date) return "-";
  const dateObj = _isString(date) ? new Date(date) : date;
  return dateObj.toLocaleString();
};

// Funcion para convertir las keys de un objeto, array de objetos, o un string
// a snake o camel case dependiendo de la función que sea pasada,
// usar los metodos snakeCase y camelCase declarados abajo
const caseConvertor = (caseFunction) => {
  return function convertCase(object) {
    if (!object) return object;
    if (_isString(object)) return caseFunction(object);
    if (_isNil(object)) return object;
    if (_isArray(object)) {
      // Si es un array vacio o ninguno de sus elementos es un objeto, lo regresa tal cual
      if (object.length > 0 && !object.some(_isObject)) return object;
      return object.map(convertCase); // Si es un array, regresa un array al que se le aplica esta función a cada elemento
    }
    return Object.keys(object) // Saca las keys del objeto en un array
      .reduce(
        (acc, key) => ({
          ...acc, // Regresa un objeto al que acumulativamente se le asigna un elemento por cada key mapeada
          [caseFunction(key)]: _isObject(object[key]) // Regresa el key en snake o camel case dependiendo de la función utilizada
            ? convertCase(object[key]) // Si el valor de esa key es otro objeto, se le aplica esta función a ese objeto
            : object[key], // Si no, se le asigna el valor que ya tenia en el objeto original
        }),
        {}
      );
  };
};

export const camelCase = caseConvertor(_camelCase);
export const snakeCase = caseConvertor(_snakeCase);

// Usa el API del navegador para convertir el archivo a base 64
/**
 *
 * @typedef {{content: string, name: string, type: string}} IFile
 * @param {File} file
 * @returns {Promise<IFile>}
 */
export const toBase64 = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) reject(new Error("No se pudo leer el archivo"));
    const reader = new FileReader();

    /**
     * @params {Event} e
     */
    const onRead = (e) => {
      resolve({
        content: e.target.result,
        name: file.name || "",
        type: file.type,
      });
    };

    reader.onloadend = onRead;

    reader.onerror = (error) => {
      reject(error);
      reader.abort();
    };

    // @ts-ignore
    reader.readAsDataURL(file);
  });
};

// Crea un array de promesas y espera a que se resuelvan todas
export const filesToB64 = (files) =>
  Promise.all(Array.from(files).map(toBase64));

/**
 * extract url from google store api
 */
const regexFile = /[^/]+(?=\?[^/]*$)/gm;

/**
 * get mime type from google store api
 * @param {string} url
 * @returns string
 * @see https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types
 */
export const getMimeTypeFromURL = (url) => {
  const [name = ""] = url.match(regexFile) || [];
  const [, extention] = name.split(".");

  return MIME_TYPES[extention];
};

/**
 * @param {string} value
 * @returns {string}
 * @example
 * capitalizeFirstLetter('HOLA MUNDO') //Hola Mundo
 */
export const capitalizeFirstLetter = (value) => _startCase(_lowerCase(value));

/**
 * Get full name client
 * @param {{names?: string, first_surname?: string, second_surname?: string}} client
 * @returns {string}
 * @example
 *  getName({names: 'MAYERLY', first_surname: 'SOTO', second_surname: 'RODRIGEZ'})
 *  // Mayerly Soto Rodriguez
 */
export const getName = (client = {}) => {
  return capitalizeFirstLetter(`
    ${client.names || "Nombre no disponible"}
    ${client.first_surname || ""}
    ${client.second_surname || ""}
  `);
};

/**
 * Download a file with a given name
 * @param {string} url
 * @param {string} name
 */
export const download = (url, name) => {
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

/**
 *
 * @param {Object} a
 * @param {Object} b
 * @returns Boolean
 */
export function areEqualShallow(a, b) {
  for (let key in a) {
    if (!(key in b) || a[key] !== b[key]) {
      return false;
    }
  }

  for (let key in b) {
    if (!(key in a) || a[key] !== b[key]) {
      return false;
    }
  }

  return true;
}

export const decode = (token) => {
  const data = token.split(".")[1];

  const decoded = decodeURIComponent(
    Array.prototype.map
      .call(
        window.atob(data.replace(/_/g, "/").replace(/-/g, "+")),
        (c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)
      )
      .join("")
  );

  return JSON.parse(decoded);
};

export const noop = () => {};

/**
 *
 * @param {string} url
 * @param {string} name
 */
export const getQueryParam = (url, name) => {
  const param = url.match(new RegExp("[?&]" + name + "=([^&#]*)"));
  return param && param[1];
};

/**
 * regresa un string con la gramática correcta de una palabra, dependiendo si es plural o singular
 * @param {number} number
 * @param {{singular: string, plural: string}} reference
 * @returns string
 * @example
 * pluralize(1, {singular: "cliente", plural: "clientes"})
 * // '1 cliente'
 */
export const pluralize = (number, reference) => {
  if (!number) {
    return "";
  }
  return number === 1
    ? `${number} ${reference.singular}`
    : `${number} ${reference.plural}`;
};

/**
 * toma un string (tipo money del backend) y lo regresa como número
 * @param {string} money
 * @returns number
 * @example
 * numberify("$10,000.00 USD")
 * // 10000
 * numberify("$20.000,00 COP")
 * // 20000
 */
export const numberify = (money) => {
  const amount = money.replace(/[^.,\d]/g, "");
  const match = amount.match(/^(.*)[.,](\d{0,2})$/);
  const numberString = match
    ? `${match[1].replace(/\D/g, "")}.${match[2]}`
    : amount.replace(/\D/g, "");

  return Number(numberString);
};

// Recibe un número y regresa un string con el número en formato de 2 digitos
export const toTwoDigits = (n) => (n >= 10 ? `${n}` : `0${n}`);

// Genera un identificador uuidv4
export const uuidv4 = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0;

    var v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Convierte un dataURI a binario
export const convertDataURIToBinary = (dataURI) => {
  var base64Index = dataURI.indexOf(";base64,") + ";base64,".length;
  var base64 = dataURI.substring(base64Index);
  var raw = window.atob(base64);
  var rawLength = raw.length;
  var array = new Uint8Array(new ArrayBuffer(rawLength));

  for (let i = 0; i < rawLength; i++) {
    array[i] = raw.charCodeAt(i);
  }
  return array;
};

// Obtiene la extensión de un archivo dado una url
/**
 * * @example
 * getExtFromUrl("https://example.com/folder/file.jpg?param.eter#hash=12.345")
 * // jpg
 */
export const getExtFromUrl = (url) => {
  return url.split(/\?/)[0].split(".").pop().trim();
};

// Obtiene el nombre de un archivo dado una url
/**
 * * @example
 * getNameFromUrl("https://example.com/folder/file.jpg?param.eter#hash=12.345")
 * // file.jpg
 */
export const getNameFromUrl = (url) => {
  return url.split(/\?/)[0].split("/").pop();
};

// Devuelve una fecha en la zona horaria dada
export const toTimeZone = (date, timezone) => {
  const utc = moment.utc(date);
  return momentTimezone.tz(utc, timezone);
};

// Devuelve una url http a su equivalente en webSockets
export const httpToWebSocket = (url) => {
  return url.replace(/(http:|https:)/, (g1) => {
    if (g1 === "http:") {
      return "ws:";
    } else {
      return "wss:";
    }
  });
};

// Toma un arreglo y regresa una copia de este sin duplicados
export const uniq = (array) => Array.from(new Set(array));

/**
 * Toma un objecto e intenta acceder al path de propiedades especificado,
 * regresando undefined si no encuentra cualquiera de las propiedades.
 * e.g.
 * safeNavigation({ a: { b: c: { d: 1 } } }, 'a.b.c.d')
 * // 1
 * safeNavigation({ a: 2 }, 'a.b.c.d')
 * // undefined
 */
export const safeNavigation = (obj, path) => {
  if (!obj || !path) return obj;

  const dotIndex = path.indexOf(".");

  if (dotIndex < 0) return obj[path];

  const propName = path.slice(0, dotIndex);

  return (
    propName in obj && safeNavigation(obj[propName], path.slice(dotIndex + 1))
  );
};

export const mapTake = (map, keys) => pick(map, keys);

export const findLabel = (options, id) => {
  const result = options.find((option) => option.id === id);
  return result ? result.label : "N/A";
};

export const showNotification = (title, message, type) => {
  Store.addNotification({
    title: title,
    message: message,
    type: type,
    insert: "top",
    container: "top-right",
    animationIn: ["animate__animated", "animate__fadeIn"],
    animationOut: ["animate__animated", "animate__fadeOut"],
    dismiss: {
      duration: 5000,
      onScreen: true,
    },
  });
};

export const getFormattedCurDate = (currentDate) => {
  currentDate.setMinutes(currentDate.getMinutes() - 60);

  currentDate.setUTCHours(currentDate.getUTCHours() - 5);

  const year = currentDate.getUTCFullYear();
  const month = String(currentDate.getUTCMonth() + 1).padStart(2, "0");
  const day = String(currentDate.getUTCDate()).padStart(2, "0");

  const hours = String(currentDate.getUTCHours()).padStart(2, "0");
  const minutes = String(currentDate.getUTCMinutes()).padStart(2, "0");
  const seconds = String(currentDate.getUTCSeconds()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

export const getFormattedCurDateAdmin = (currentDate) => {
  const options = { timeZone: "America/Bogota" };
  currentDate.toLocaleDateString("en-US", options);
  currentDate.toLocaleTimeString("en-US", options);

  const year = currentDate.getFullYear();
  const month = String(currentDate.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const day = String(currentDate.getDate()).padStart(2, "0");

  const hours = String(currentDate.getHours()).padStart(2, "0");
  const minutes = String(currentDate.getMinutes()).padStart(2, "0");
  const seconds = String(currentDate.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

export const hasUserRole = (rolesToCheck) => {
  const token = Cookies.get("token");
  if (token) {
    try {
      const decodedToken = decode(token);
      if (decodedToken) {
        return rolesToCheck.some((item) =>
          decodedToken.authorities.includes(item)
        );
      }
    } catch (error) {
      console.error("Error decoding token:", error);
    }
  }
  return false;
};

export const getStudentId = () => {
  const token = Cookies.get("token");
  if (token) {
    try {
      const decodedToken = decode(token);
      if (decodedToken && decodedToken.sub) {
        return decodedToken.sub;
      }
    } catch (error) {
      console.error("Error decoding token:", error);
    }
  }
  return null;
};
